import type { WindowWithIcbm } from "../index";
import type {
  BucketingMatcherSelector,
  GroupMarker,
  Matcher,
} from "../matchers";
import { localStorageHelper, type LocalStorageHelper } from "../storage";
import { fetchTimeout } from "utils";
import type {
  InstantConfigGroup,
  InstantConfigResponse,
  InstantConfigValue,
  RegularInstantConfigBucketing,
} from "./types";
import { mapToRegularBucketing } from "./mapper";

export function shouldBeListOfConfigGroups(
  obj: unknown,
): obj is InstantConfigGroup[] {
  return Array.isArray(obj) && obj.length > 0 && typeof obj[0] === "object";
}

export function getSampling(g: InstantConfigGroup): number | null {
  const sampling = g.sampling ?? g.s;

  if (typeof sampling === "number" && sampling > 0 && sampling < 100) {
    return sampling;
  }

  return null;
}

/**
 * Returns regular bucketing configuration for a given group
 * @param g
 */
export function getBucketing(
  g: InstantConfigGroup,
): RegularInstantConfigBucketing | undefined {
  return (
    g?.bucketing ??
    (g?.bu !== undefined ? mapToRegularBucketing(g.bu) : undefined)
  );
}

/**
 * Currently, the code supports both `sampling` and `bucketing` fields to ensure backward compatibility during the transition period.
 * Eventually, `sampling` will be removed, and only `bucketing` will remain.
 */
export const getValue = (
  config: InstantConfigResponse,
  matchers: Matcher[],
  key: string,
  deprecatedSamplingMatcher?: Matcher,
  groupMarker?: GroupMarker,
  bucketingMatcherSelector?: BucketingMatcherSelector,
  // eslint-disable-next-line max-params
): InstantConfigValue | undefined => {
  const value = config[key];

  if (shouldBeListOfConfigGroups(value)) {
    // eslint-disable-next-line complexity
    const group = value.find((group) => {
      const result = matchers.reduce(
        (result, matcher) => result && matcher(group),
        true,
      );

      const bucketing = getBucketing(group);

      if (bucketing && bucketingMatcherSelector && result) {
        const bucketingMatcher = bucketingMatcherSelector(bucketing.source);
        const bucketingMatcherResult = bucketingMatcher(group);

        if (bucketingMatcherResult) {
          groupMarker?.mark(bucketingMatcherResult);
          return bucketingMatcherResult.isActive;
        }
      }

      /**
       * @deprecated
       */
      const sampling = getSampling(group);

      if (sampling === null || !result || !deprecatedSamplingMatcher) {
        return result;
      }

      // At this point, we are sure that either group.s or group.sampling is present
      // as this has been checked by hasSampling function
      const active = deprecatedSamplingMatcher(group);
      if (groupMarker) {
        groupMarker.deprecatedMark(key, sampling, active);
      }
      return active;
    });

    return group?.v ?? group?.value;
  }

  return value;
};

export const fetchInstantConfig = async (
  container: WindowWithIcbm,
  app: string,
): Promise<InstantConfigResponse> => {
  const instantConfig = container.instantConfig;
  if (instantConfig) {
    return instantConfig;
  }

  const url = `https://services.fandom.com/icbm/api/config?app=${app}&compact=true`;
  const fallbackUrl = `https://script.wikia.nocookie.net/fandom-ae-assets/icbm/prod/icbm_state_${app}.json`;
  const storageAdapter = localStorageHelper(
    `instant-config-${app}`,
    60000,
    container,
  );

  const config =
    (await getCachedConfig(url, storageAdapter)) ??
    (await fetchConfig(fallbackUrl)) ??
    {};
  container.instantConfig = config;
  return config;
};

const getCachedConfig = async (
  url: string,
  storage: LocalStorageHelper,
): Promise<InstantConfigResponse | undefined> => {
  const cached = storage.get();
  if (cached) {
    return JSON.parse(cached);
  }

  const config = await fetchConfig(url);
  if (config) {
    storage.set(JSON.stringify(config));
  }
  return config;
};

const fetchConfig = async (
  url: string,
): Promise<InstantConfigResponse | undefined> => {
  try {
    const response = await fetchTimeout(url, 2000);

    if (!response.ok) {
      return undefined;
    }

    return response.json();
  } catch {
    return undefined;
  }
};
